<template>
  <AppLayout
    :access-status="!currentUserChecked ? 'pending' : isAuthenticated ? 'granted' : 'blocked'"
    :warning-message="city?.isDemoMode ? `${city.name} is in demo mode.` : null"
    :wide-left-bar="hasSelection"
    :show-detail="hasSelection"
    :show-list="!hasSelection"
    show-map
  >

    <template #top>
      <TopBar
        :city="city"
        selected-tab="violations"
        :buttons="buttons"
        :show-search="!hasSelection"
        @search="search"
        search-placeholder="Search violations"
      />
      <div v-if="exportError || mapError || printError" class="text-danger p-3" role="alert">
        {{ (exportError || mapError || printError).message }}
        <button v-if="mapError" class="btn btn-link" @click="reloadLicenses">Retry map</button>
      </div>
    </template>

    <template v-if="hasSelection" #detail>
      <ViolationDetailView
        :city="city"
        :violation-id="selectedViolationId"
        :violation-summary="selectedViolation"
        :panel="panel"
        @update:panel="updatePanel"
        @open-street-view="openStreetView"
        @show-location="showLocation"
        @close="closeDetail"
        class="w-100"
      >
        <template #navigation>
          <IconButton
            icon="x-lg"
            title="Back to list"
            class="flex-shrink-0"
            @click="closeDetail"
          />
          <div class="text-muted small px-2">
            <template v-if="selectedResultIndex >= 0">{{ selectedResultIndex + 1 }} of {{ total }}</template>
            <template v-else>Outside current results</template>
          </div>
          <IconButton button :disabled="filtering || !total" icon="chevron-up" class="flex-shrink-0" @click="() => nextViolation(true)" />
          <IconButton button :disabled="filtering || !total" icon="chevron-down" class="flex-shrink-0" @click="() => nextViolation(false)" />
        </template>
      </ViolationDetailView>
    </template>

    <template #list>
      <FilterableList
        :count="total"
        :page-start="pageStart"
        :page-end="pageEnd"
        @previous-page="changePage(previousCursor)"
        @next-page="changePage(nextCursor)"
        @updated="applyFilterChanges"
        @canceled="cancelFilterChanges"
        @reset="resetFilters"
        :noun="listNoun"
        :query="query"
        :loading="!loaded || filtering"
        :show-header="!hasSelection"
        :active-filter-count="activeFilterCount"
      >

        <template #filters v-if="draftFilters">
          <FilterEditor :filters="draftFilters" :sections="violationFilterSections" />
        </template>

        <template #list>
          <div class="d-flex flex-row align-items-stretch h-100">
            <div class="w-100">
              <LoadingMessage v-if="!loaded" message="Loading violations"/>
              <LoadingMessage v-else-if="filtering" message="Updating violations" />
              <BCollapse :show="loaded && !filtering && violations.length === 0">
                <div class="d-flex flex-column align-items-center p-4">
                  <h6 v-if="loadError" class="text-center text-danger">
                    An error occurred while loading violations: {{ String(loadError) }}
                    <button class="btn btn-link" @click="city ? reloadResults() : reloadCity()">Retry</button>
                  </h6>
                  <template v-else>
                    <h6 class="text-center">
                      No violations have been flagged yet.
                    </h6>
                    <p class="text-center">
                      Once violations have been flagged, this area will show them.
                    </p>
                  </template>
                </div>
              </BCollapse>

              <PropertyList
                v-if="!filtering"
                :properties="violations"
                @select="selectViolation"
              />
            </div>
          </div>
        </template>
      </FilterableList>
    </template>

    <template #map>
      <GMap
        ref="map"
        @deselect="selectViolation(null)"
        :city="city"
        :selected-marker-id="selectedViolationId"
        :items="mapItems"
        disable-left-bar-toggle
      >
        <template #top-left>
          <div class="bg-white" style="margin: 10px;border-radius:2px;box-shadow:0px 1px 3px rgba(0,0,0,0.2)">
            <button
              type="button"
              :class="`btn btn-flat ${showLicenses ? '' : 'text-muted fw-normal'}`"
              style="height:40px;line-height:40px;padding:0 10px;"
              @click="showLicenses = !showLicenses">
              <i :class="`bi-${showLicenses ? 'check-' : ''}square me-1`"/> {{ loadingMap ? 'Loading Licenses…' : 'Show Licenses' }}
            </button>
          </div>
        </template>
        <template #marker="{item}">
          <GMapPropertyMarker v-if="item instanceof Property" :property="item" :selected-id="selectedViolationId" />
          <GMapLicenseMarker v-else-if="item instanceof License" :license="item" :selected-id="null" />
        </template>
        <template #info="{data:violation}">
          <LicenseItemView v-if="violation instanceof License" :license="violation" />
          <PropertyItemView v-else :property="violation" show-listing-count />
        </template>
      </GMap>
    </template>
  </AppLayout>

  <LetterPreviewModal
    v-if="pendingLetterItems.length"
    :items="pendingLetterItems"
    v-model="showPendingLettersModal"
    print-on-ok
  />

</template>

<script>
import AppLayout from '@/components/common/AppLayout.vue';
import TopBar from '@/components/common/TopBar.vue';
import GMap from '@/components/common/GMap.vue';
import BCollapse from '@/components/common/BCollapse.vue';
import IconButton from '@/components/common/IconButton.vue';
import LoadingMessage from '@/components/common/LoadingMessage.vue';
import FilterableList from '@/components/common/FilterableList.vue';
import { Property } from '@/models/Property';
import PropertyList from '@/components/PropertyList.vue';
import ViolationDetailView from '@/components/ViolationDetailView.vue';
import GMapPropertyMarker from '@/components/common/GMapPropertyMarker.vue';
import PropertyItemView from '@/components/PropertyItemView.vue';
import LicenseItemView from '@/components/LicenseItemView.vue';
import FilterEditor from '@/components/common/FilterEditor.vue';
import violationFilterSections from '@/components/violations/violationFilterSections';
import { ViolationReason } from '@strenforcement/domain/violation';
import { License } from '@/models/License';
import GMapLicenseMarker from '@/components/common/GMapLicenseMarker.vue';
import LetterPreviewModal from '@/components/letters/LetterPreviewModal.vue';
import { propertyRepository } from '@/data';
import { buildLetterContextForCorrespondence } from '@/models/letterContext';
import { createCitySearchMixin } from '@/citySearch';

const defaults = () => ({
  seen: false, deferred: 'not-deferred', deferralEndedWithinDays: null, letters: 'all', sentLetters: 'all',
  reasons: Object.fromEntries(Object.values(ViolationReason).map(reason => [reason, true])),
  sort: { field: 'detected', ascending: false }
});
const copyFilters = filters => ({ ...filters, reasons: { ...filters.reasons }, sort: { ...filters.sort } });

export default {
  mixins: [createCitySearchMixin({
    itemsProperty: 'violations', selectedIdProperty: 'selectedViolationId', Model: Property,
    search: (cityId, options) => propertyRepository.searchViolations(cityId, options), selectMethod: 'selectViolation'
  })],
  components: {
    LetterPreviewModal, GMapLicenseMarker, FilterEditor, PropertyItemView, LicenseItemView,
    GMapPropertyMarker, ViolationDetailView, PropertyList, FilterableList, LoadingMessage,
    IconButton, BCollapse, GMap, TopBar, AppLayout
  },
  props: { cityId: String, selectedViolationId: String, panel: String },
  data() {
    return { defaultFilters: defaults(), filters: defaults(), draftFilters: null,
      pendingLetterItems: [], showPendingLettersModal: false, printingLetters: false, printError: null };
  },
  watch: {
    filters: { deep: true, handler() {
      this.draftFilters = copyFilters(this.filters);
      this.pendingLetterItems = [];
      this.showPendingLettersModal = false;
    } }
  },
  computed: {
    violationFilterSections() { return violationFilterSections; },
    listNoun() {
      if (this.filters.deferred === 'deferred') return 'deferred violation';
      if (this.filters.deferred === 'previously-deferred') return 'previously deferred violation';
      return 'violation';
    },
    buttons() {
      const buttons = this.isAuthenticated ? [{
        id: 'export', click: () => this.exportViolations(), icon: 'download', showOnMobile: true,
        tooltip: this.exporting ? 'Preparing CSV export…' : 'Export all matching violations to CSV',
        text: this.exporting ? 'Exporting…' : null
      }] : [];
      if (this.searchMetadata.pendingLetterCount > 0) buttons.push({
        id: 'print', icon: 'printer', click: () => this.preparePendingLetters(),
        tooltip: `Print ${this.searchMetadata.pendingLetterCount} pending letters across all matching violations`,
        text: this.printingLetters ? 'Preparing letters…' : null
      });
      return buttons;
    },
    hasSelection() { return Boolean(this.selectedViolationId); },
    selectedViolation() { return this.violations.find(item => item.id === this.selectedViolationId) || null; },
    mapItems() {
      const items = this.filtering ? [] : [...this.violations];
      if (this.showLicenses) items.push(...this.licenses);
      return items;
    },
    activeFilterCount() {
      const f = this.filters, d = this.defaultFilters;
      return ['seen', 'deferred', 'letters', 'sentLetters'].filter(key => f[key] !== d[key]).length
        + Number(f.sort.field !== d.sort.field || f.sort.ascending !== d.sort.ascending)
        + Number(f.deferred === 'previously-deferred' && f.deferralEndedWithinDays !== d.deferralEndedWithinDays)
        + Number(Object.keys(d.reasons).some(key => f.reasons[key] !== d.reasons[key]));
    },
    Property() { return Property; },
    License() { return License; }
  },
  methods: {
    searchOptions() {
      const { sort, ...filters } = this.filters;
      return { ...filters, reasons: { ...filters.reasons }, query: this.query, sortField: sort.field, sortAscending: sort.ascending };
    },
    onSearchReset() {
      this.pendingLetterItems = [];
      this.showPendingLettersModal = false;
      this.printError = null;
    },
    applyFilterChanges() { this.filters = copyFilters(this.draftFilters); return this.filterResults(); },
    cancelFilterChanges() { this.draftFilters = copyFilters(this.filters); },
    resetFilters() { this.draftFilters = copyFilters(this.defaultFilters); },
    nextViolation(backward) { return this.nextResult(backward); },
    showLocation(latlng) { this.$refs.map?.setCenter(latlng, 18); },
    openStreetView(latlng) { this.$refs.map?.openStreetView(latlng); },
    updatePanel(panel) { this.$router.replace({ query: { ...this.$route.query, panel: panel || undefined } }); },
    closeDetail(message) {
      this.selectViolation(null);
      if (message?.title && message?.body) this.$showToast(`<div class="fw-bold">${message.title}</div><div>${message.body}</div>`);
    },
    selectViolation(id) {
      this.$router.push(id
        ? { name: 'violation-detail', params: { selectedViolationId: id }, query: this.$route.query }
        : { name: 'violations', query: this.$route.query });
    },
    async preparePendingLetters() {
      if (this.printingLetters || !this.city || !this.isAuthenticated) return;
      const city = this.city, cityToken = this.cityLoadToken, loadToken = this.loadToken;
      this.printingLetters = true;
      this.printError = null;
      try {
        const { items } = await propertyRepository.pendingViolationLetters(city.id, this.searchOptions());
        if (cityToken !== this.cityLoadToken || loadToken !== this.loadToken || !this.isAuthenticated) return;
        this.pendingLetterItems = items.map(Property.dataConverter(city)).flatMap(property => property.correspondences
          .filter(letter => letter.isPrinted)
          .map(letter => ({ letter, context: buildLetterContextForCorrespondence(letter) }))
          .filter(item => item.context?.city && item.context?.property));
        this.showPendingLettersModal = this.pendingLetterItems.length > 0;
        if (!this.pendingLetterItems.length) this.printError = new Error('There are no pending letters for the current filters.');
      } catch (error) {
        if (cityToken === this.cityLoadToken && loadToken === this.loadToken) this.printError = error;
      } finally { this.printingLetters = false; }
    },
    exportViolations() {
      return this.downloadExport({
        fetch: (cityId, options) => propertyRepository.exportViolations(cityId, options), filename: 'violations',
        headers: ['License Number', 'Date Issued', 'Status', 'Parcel ID', 'Street Number', 'Street Name', 'Unit', 'Owner', 'Mailing Address', 'Email', 'Phone'],
        rows: violations => violations.map(violation => [
          violation.number + ' ', this.formatDate(violation.dateIssued, ' '), violation.status, violation.id,
          violation.address?.streetNumber || '-', violation.address?.street || '-', violation.address?.unit || '',
          violation.owners.join(', '), violation.ownerAddress, violation.contact?.email, violation.contact?.phone
        ])
      });
    }
  }
};
</script>
